import { useCallback, useEffect, useMemo, useState } from 'react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { getMarketplaceGraphqlClient } from 'modules/it_management/lib/marketplaceGraphqlClient'
import {
  DELETE_MARKETPLACE_DEVICE,
  GET_MARKETPLACE_SAVED_DEVICES,
  SAVE_MARKETPLACE_DEVICE,
  type MarketplaceSavedDevice,
} from 'modules/it_management/lib/marketplaceSavedDevicesGraphql'

type SavedDevicesQueryPayload = {
  savedDevices?: MarketplaceSavedDevice[] | null
}

type SaveDevicePayload = {
  saveDevice?: MarketplaceSavedDevice | null
}

type DeleteDevicePayload = {
  deleteDevice?: boolean
}

export function useDeviceCatalogSavedDevices() {
  const {
    company: { id: companyIdRaw },
  } = useCurrentSession()
  const companyId = Number(companyIdRaw)
  const marketplaceClient = getMarketplaceGraphqlClient()

  const [savedIds, setSavedIds] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [pendingIds, setPendingIds] = useState<string[]>([])

  const refreshSavedDevices = useCallback(async () => {
    if (Number.isNaN(companyId)) {
      setSavedIds([])
      setLoading(false)
      return
    }

    setLoading(true)

    try {
      const result = await marketplaceClient.query<SavedDevicesQueryPayload>({
        query: GET_MARKETPLACE_SAVED_DEVICES,
        variables: { companyId },
      })
      const ids = (result.data?.savedDevices ?? []).map((item) => item.productVariantId)
      setSavedIds(ids)
    } catch {
      setSavedIds([])
    } finally {
      setLoading(false)
    }
  }, [companyId, marketplaceClient])

  useEffect(() => {
    void refreshSavedDevices()
  }, [refreshSavedDevices])

  const toggleSaved = useCallback(
    async (productVariantId: string) => {
      if (Number.isNaN(companyId) || pendingIds.includes(productVariantId)) return

      setPendingIds((prev) => [...prev, productVariantId])

      try {
        const isCurrentlySaved = savedIds.includes(productVariantId)

        if (isCurrentlySaved) {
          const result = await marketplaceClient.mutate<DeleteDevicePayload>({
            mutation: DELETE_MARKETPLACE_DEVICE,
            variables: { productVariantId },
            refetchQueries: [
              {
                query: GET_MARKETPLACE_SAVED_DEVICES,
                variables: { companyId },
              },
            ],
          })

          if (result.data?.deleteDevice) {
            setSavedIds((prev) => prev.filter((id) => id !== productVariantId))
          }
        } else {
          const result = await marketplaceClient.mutate<SaveDevicePayload>({
            mutation: SAVE_MARKETPLACE_DEVICE,
            variables: { productVariantId },
            refetchQueries: [
              {
                query: GET_MARKETPLACE_SAVED_DEVICES,
                variables: { companyId },
              },
            ],
          })

          if (result.data?.saveDevice?.productVariantId) {
            setSavedIds((prev) => {
              if (prev.includes(productVariantId)) return prev
              return [...prev, productVariantId]
            })
          }
        }
      } finally {
        setPendingIds((prev) => prev.filter((id) => id !== productVariantId))
      }
    },
    [companyId, marketplaceClient, pendingIds, savedIds]
  )

  const isSaved = useCallback(
    (productVariantId: string) => savedIds.includes(productVariantId),
    [savedIds]
  )

  const isPending = useCallback(
    (productVariantId: string) => pendingIds.includes(productVariantId),
    [pendingIds]
  )

  return useMemo(
    () => ({
      savedIds,
      loading,
      toggleSaved,
      isSaved,
      isPending,
      refreshSavedDevices,
    }),
    [savedIds, loading, toggleSaved, isSaved, isPending, refreshSavedDevices]
  )
}
